import { MAX_CARD_LIMIT } from '../constants'
import type { RainFrequency } from '../hooks/useBorrowLimit'

export interface CardLimitTier {
  name: string
  dailyLimitCents: number
  usedTodayCents: number
  remainingTodayCents: number
  maxInflight: number
}

const PERIOD_DAYS: Partial<Record<RainFrequency, number>> = {
  per24HourPeriod: 1,
  per7DayPeriod: 7,
  per30DayPeriod: 30,
  perYearPeriod: 365,
  perAuthorization: 1,
}

export function getFrequencyLabel(frequency: RainFrequency): string {
  switch (frequency) {
    case 'per24HourPeriod':
      return 'daily'
    case 'per7DayPeriod':
      return 'weekly'
    case 'per30DayPeriod':
      return 'monthly'
    case 'perYearPeriod':
      return 'yearly'
    case 'allTime':
      return 'lifetime'
    case 'perAuthorization':
      return 'per charge'
  }
}

export function getSelectedPeriodMaxUSD({
  tier,
  frequency,
  globalMaxUSD = MAX_CARD_LIMIT,
}: {
  tier?: CardLimitTier
  frequency: RainFrequency
  globalMaxUSD?: number
}): number {
  const baseMax = globalMaxUSD

  if (!tier) {
    return baseMax
  }

  // Lifetime limits are configured independently from the backend's rolling daily
  // tier cap. Daily spend enforcement still applies when transactions are authorized.
  if (frequency === 'allTime') {
    return baseMax
  }

  const days = PERIOD_DAYS[frequency]
  if (!days) {
    return baseMax
  }

  const tierPeriodMax = (tier.dailyLimitCents / 100) * days
  if (!Number.isFinite(tierPeriodMax) || tierPeriodMax < 0) {
    return baseMax
  }

  return Math.min(tierPeriodMax, baseMax)
}

export function formatUSD(amount: number): string {
  return amount.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function readFiniteNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined
  }

  return value
}

export function normalizeCardLimitTier(raw: unknown): CardLimitTier | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined
  }

  const candidate = raw as Record<string, unknown>
  const dailyLimitCents = readFiniteNumber(candidate.dailyLimitCents)
  const usedTodayCents = readFiniteNumber(candidate.usedTodayCents) ?? 0
  const remainingTodayCents = readFiniteNumber(candidate.remainingTodayCents) ?? 0
  const maxInflight = readFiniteNumber(candidate.maxInflight)

  if (typeof candidate.name !== 'string' || candidate.name.trim() === '') {
    return undefined
  }
  if (dailyLimitCents === undefined || dailyLimitCents < 0 || maxInflight === undefined || maxInflight < 0) {
    return undefined
  }

  return {
    name: candidate.name.trim(),
    dailyLimitCents,
    usedTodayCents: Math.max(0, usedTodayCents),
    remainingTodayCents: Math.max(0, remainingTodayCents),
    maxInflight: Math.floor(maxInflight),
  }
}

export function getTierLimitSummary(tier: CardLimitTier | undefined): string {
  if (!tier) {
    return ''
  }

  return `${tier.name} tier`
}

export function getTierLimitTooltip(
  tier: CardLimitTier | undefined,
  frequency: RainFrequency,
  selectedMaxUSD: number,
): string {
  if (!tier) {
    return ''
  }

  const dailyMaxUSD = tier.dailyLimitCents / 100
  const dailyCap = `$${formatUSD(dailyMaxUSD)}`
  const inflightCopy = `You can have up to ${tier.maxInflight} in-flight ${
    tier.maxInflight === 1 ? 'transaction' : 'transactions'
  }.`

  if (frequency === 'per24HourPeriod') {
    return `${tier.name} tier is capped at ${dailyCap}/day. ${inflightCopy}`
  }
  if (frequency === 'perAuthorization') {
    return `${tier.name} tier is capped at ${dailyCap}/day. Single charges above this may be declined. ${inflightCopy}`
  }
  if (frequency === 'allTime') {
    return `Lifetime limit is capped by the card maximum. Spending is still capped at ${dailyCap}/day. ${inflightCopy}`
  }

  return `${tier.name} tier is capped at ${dailyCap}/day, so ${getFrequencyLabel(
    frequency,
  )} limits can be up to $${formatUSD(selectedMaxUSD)}. ${inflightCopy}`
}

export function getLimitCapError(
  tier: CardLimitTier | undefined,
  frequency: RainFrequency,
  selectedMaxUSD: number,
): string {
  const cap = `$${formatUSD(selectedMaxUSD)}`

  if (!tier) {
    return `Limit cannot exceed ${cap}.`
  }

  const dailyCap = `$${formatUSD(tier.dailyLimitCents / 100)}`
  if (frequency === 'per24HourPeriod' || frequency === 'perAuthorization') {
    return `${tier.name} tier is capped at ${dailyCap}/day. Enter ${cap} or less.`
  }
  if (frequency === 'allTime') {
    return `Limit cannot exceed ${cap}. Spending is still capped at ${dailyCap}/day.`
  }

  return `${tier.name} tier is capped at ${dailyCap}/day, so ${getFrequencyLabel(
    frequency,
  )} limits can be up to ${cap}.`
}
